import React, {ReactNode, useMemo} from "react";
import styled from "styled-components";
import {FaBurn, FaExclamationTriangle} from "react-icons/fa";
import {CheatPlacementOptions, EmptySlot} from "../SharedComponents/EmptySlot";
import {useInventory} from "../State/InventoryContext";
import {POWER_CONSTANTS} from "../State/GameData";
import {FlowBar} from "../SharedComponents/FlowBar";

const P = {
    Page: styled.div`
      padding: 16px;
    `,
    Schematic: styled.div`
      display: grid;
      gap: 16px;
    `,
    Node: styled.div<{background?: string}>`
      border: 1px solid #ced4da;
      border-radius: 12px;
      padding: 16px;
      background: ${({background}) => background ?? "#fff"};
      box-shadow: 0 8px 18px rgba(33, 37, 41, 0.06);
    `,
    NodeHeader: styled.div`
      display: flex;
      align-items: center;
      gap: 12px;
      margin-bottom: 12px;
      flex-wrap: wrap;
    `,
    NodeTitle: styled.div`
      font-size: 18px;
      font-weight: 700;
      color: #212529;
      display: flex;
      align-items: center;
      gap: 8px;
    `,
    NodeMeta: styled.div`
      color: #495057;
      font-size: 14px;
    `,
    Icon: styled.div<{tone?: string}>`
      width: 36px;
      height: 36px;
      border-radius: 50%;
      background: ${({tone}) => tone ?? "#e7f5ff"};
      display: grid;
      place-items: center;
      font-size: 18px;
      border: 2px solid rgba(0, 0, 0, 0.06);
      position: relative;
    `,
    IconBadge: styled.span`
      position: absolute;
      right: -6px;
      bottom: -6px;
      background: #212529;
      color: #ffc078;
      width: 20px;
      height: 20px;
      border-radius: 50%;
      display: grid;
      place-items: center;
      border: 2px solid #fff;
      font-size: 12px;
    `,
    FieldSurface: styled.div<{pattern?: string}>`
      border-radius: 10px;
      border: 1px dashed rgba(0, 0, 0, 0.1);
      padding: 12px;
      background: ${({pattern}) => pattern ?? "#f8f9fa"};
    `,
    StackGrid: styled.div`
      display: grid;
      gap: 10px;
      grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
      margin-top: 12px;
    `,
    StackCard: styled.div`
      background: rgba(255, 255, 255, 0.65);
      border-radius: 10px;
      padding: 10px;
      border: 1px solid rgba(0, 0, 0, 0.06);
      box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.03);
    `,
    StackTitle: styled.div`
      font-weight: 700;
      color: #212529;
      margin-bottom: 8px;
    `,
    StackSlots: styled.div`
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
    `,
    MachineChip: styled.button<{active?: boolean; tone: string}>`
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 6px 10px;
      border-radius: 8px;
      border: 1px solid ${({tone}) => tone};
      background: ${({active, tone}) => active ? `${tone}22` : "#fff"};
      color: #212529;
      cursor: pointer;
      font-weight: 600;
      transition: all 0.2s ease;
      min-width: 100px;
      justify-content: center;
      position: relative;
      &:hover {
        box-shadow: 0 4px 10px rgba(0,0,0,0.06);
      }
    `,
    MachineWarning: styled.span`
      position: absolute;
      top: -6px;
      right: -6px;
      background: #fff4e6;
      color: #d9480f;
      border: 1px solid #ffd8a8;
      width: 22px;
      height: 22px;
      border-radius: 50%;
      display: grid;
      place-items: center;
      font-size: 12px;
    `,
    AddSlot: styled.div`
      width: 100px;
      height: 38px;
      display: flex;
      align-items: center;
      justify-content: center;
    `,
    Blurb: styled.p`
      color: #495057;
      margin: 8px 0 0;
    `,
};

type PowerMachineKey = "waterPumps" | "waterBoilers" | "steamGenerators";

type StackFieldProps = {
    title: string;
    machineKey: PowerMachineKey;
    optionIds: string[];
    stackSize: number;
    maxCount: number;
    icon: ReactNode;
    tone: string;
    pattern?: string;
};

type MachineState = { running: boolean } & Record<string, unknown>;

type BoilerMachineState = MachineState & { fuelStarved?: boolean };

function chunkIntoStacks<T>(items: T[], stackSize: number, maxStacks: number) {
    const stackCount = Math.max(1, Math.min(maxStacks, Math.ceil((items.length || 1) / stackSize)));
    return Array.from({length: stackCount}, (_, index) => items.slice(index * stackSize, (index + 1) * stackSize));
}

function StackField({title, machineKey, optionIds, stackSize, maxCount, icon, tone, pattern}: StackFieldProps) {
    const {inventory, addPowerMachine, togglePowerMachine, updateInventoryItem} = useInventory();
    const machines = inventory.powerMachines[machineKey] ?? [];
    const running = machines.filter(machine => (machine as MachineState).running).length;
    const availableStacks = chunkIntoStacks(machines, stackSize, Math.max(1, Math.ceil(maxCount / stackSize)));
    const options = inventory.inventory.filter(item => optionIds.includes(item.item.id));

    const addMachine = (itemId: string, options?: CheatPlacementOptions) => {
        if (machines.length >= maxCount) return;
        addPowerMachine(machineKey, itemId);
        if (!options?.isCheat) {
            updateInventoryItem(itemId, -1);
        }
    };

    return <P.FieldSurface pattern={pattern}>
        <P.NodeHeader>
            <P.NodeTitle>{icon} {title}</P.NodeTitle>
            <P.NodeMeta>{running} running / {machines.length} placed</P.NodeMeta>
        </P.NodeHeader>
        <P.StackGrid>
            {availableStacks.map((stack, stackIndex) => {
                const stackBaseIndex = stackIndex * stackSize;
                return <P.StackCard key={`${title}-stack-${stackIndex}`}>
                    <P.StackTitle>Stack {stackIndex + 1}</P.StackTitle>
                    <P.StackSlots>
                        {stack.map((machine, slotIndex) => {
                            const globalIndex = stackBaseIndex + slotIndex;
                            const isActive = (machine as MachineState).running;
                            const fuelStarved = machineKey === "waterBoilers" && !!(machine as BoilerMachineState).fuelStarved;
                            return <P.MachineChip
                                key={`${title}-${stackIndex}-${slotIndex}`}
                                active={isActive}
                                tone={tone}
                                onClick={() => togglePowerMachine(machineKey, globalIndex)}
                                aria-label={`${title} ${globalIndex + 1} ${isActive ? "on" : "off"}`}
                            >
                                <span>{icon}</span>
                                <span>{isActive ? "On" : "Off"}</span>
                                {fuelStarved && !isActive && (
                                    <P.MachineWarning title="Out of fuel">
                                        <FaExclamationTriangle />
                                    </P.MachineWarning>
                                )}
                            </P.MachineChip>;
                        })}
                        {stack.length < stackSize && machines.length < maxCount &&
                            <P.AddSlot>
                                <EmptySlot type={machineKey} clickable={true} options={options} addedItem={addMachine}/>
                            </P.AddSlot>}
                    </P.StackSlots>
                </P.StackCard>;
            })}
        </P.StackGrid>
    </P.FieldSurface>;
}

export function Production() {
    const {inventory, togglePowerMachine} = useInventory();
    const {powerStats} = inventory;

    const pump = inventory.powerMachines.waterPumps[0];
    const pumpIcon = <P.Icon tone="#d0ebff">💧</P.Icon>;
    const boilerIcon = <P.Icon tone="#c5f6fa">🔥<P.IconBadge title="Consumes fuel"><FaBurn /></P.IconBadge></P.Icon>;
    const generatorIcon = <P.Icon tone="#fff3bf">⚡</P.Icon>;

    const boilerMaxCount = POWER_CONSTANTS.boilerStackSize * 3;
    const generatorMaxCount = POWER_CONSTANTS.generatorStackSize * 3;

    const boilerStacks = Math.max(1, Math.ceil((inventory.powerMachines.waterBoilers.length || 1) / POWER_CONSTANTS.boilerStackSize));
    const generatorStacks = Math.max(1, Math.ceil((inventory.powerMachines.steamGenerators.length || 1) / POWER_CONSTANTS.generatorStackSize));

    const electricityFooter = useMemo(() => {
        const unmet = Math.max(0, powerStats.demand - powerStats.supply);
        if (unmet <= 0) {
            return "Assemblers and drills have full power";
        }
        return `Short ${unmet.toFixed(1)} u — consider adding steam engines`;
    }, [powerStats.demand, powerStats.supply]);

    return <P.Page>
        <h2>Power Production</h2>
        <p>Power supply meets {Math.round(powerStats.ratio * 100)}% of demand. Steam used: {powerStats.steamUsed.toFixed(1)} / {powerStats.steamDemand.toFixed(1)}.</p>
        <P.Schematic>
            <P.Node background="#e7f5ff">
                <P.NodeHeader>
                    {pumpIcon}
                    <div>
                        <P.NodeTitle>Water Pump</P.NodeTitle>
                        <P.NodeMeta>100 water baseline flow</P.NodeMeta>
                    </div>
                </P.NodeHeader>
                <FlowBar
                    title="Water draw"
                    value={powerStats.waterUsed}
                    capacity={powerStats.waterProduced}
                    unit=" u"
                    color="#4dabf7"
                    background="#d0ebff"
                    footer={`Feeds up to ${POWER_CONSTANTS.boilerStackSize * 3} boiler stacks`}
                />
                {pump && <P.MachineChip
                    tone="#228be6"
                    active={pump.running}
                    onClick={() => togglePowerMachine("waterPumps", 0)}
                >
                    {pumpIcon}
                    <span>{pump.running ? "Pump On" : "Pump Off"}</span>
                </P.MachineChip>}
                {pump && <P.Blurb>The pump is {pump.running ? "open and sending water downstream" : "closed"}.</P.Blurb>}
            </P.Node>

            <P.Node background="#e3fafc">
                <P.NodeHeader>
                    {boilerIcon}
                    <div>
                        <P.NodeTitle>Boiler Stacks</P.NodeTitle>
                        <P.NodeMeta>{boilerStacks} stack lanes · {POWER_CONSTANTS.boilerStackSize} boilers per stack</P.NodeMeta>
                    </div>
                </P.NodeHeader>
                <FlowBar
                    title="Steam generation"
                    value={powerStats.steamUsed}
                    capacity={Math.max(powerStats.steamCapacity, powerStats.steamDemand, 1)}
                    unit=" steam"
                    color="#63e6be"
                    background="#ccfbf1"
                    footer={`Fuel burn: ${powerStats.fuelUsedPerSecond.toFixed(2)} coal / tick`}
                />
                <StackField
                    title="Boiler"
                    machineKey="waterBoilers"
                    optionIds={["waterBoiler"]}
                    stackSize={POWER_CONSTANTS.boilerStackSize}
                    maxCount={boilerMaxCount}
                    icon={boilerIcon}
                    tone="#15aabf"
                    pattern="#e7f5ff"
                />
            </P.Node>

            <P.Node background="repeating-linear-gradient(135deg, #fff9db, #fff9db 12px, #ffe066 12px, #ffe066 24px)">
                <P.NodeHeader>
                    {generatorIcon}
                    <div>
                        <P.NodeTitle>Steam Power Generators</P.NodeTitle>
                        <P.NodeMeta>{generatorStacks} generator rows · {POWER_CONSTANTS.generatorStackSize} per row</P.NodeMeta>
                    </div>
                </P.NodeHeader>
                <FlowBar
                    title="Electricity"
                    value={powerStats.supply}
                    capacity={Math.max(powerStats.demand, powerStats.supply, 1)}
                    unit=" u"
                    color="#fcc419"
                    background="#fff3bf"
                    footer={electricityFooter}
                />
                <StackField
                    title="Steam Engine"
                    machineKey="steamGenerators"
                    optionIds={["steamGenerator"]}
                    stackSize={POWER_CONSTANTS.generatorStackSize}
                    maxCount={generatorMaxCount}
                    icon={generatorIcon}
                    tone="#f59f00"
                    pattern="repeating-linear-gradient(135deg, #fff9db, #fff9db 10px, #ffe066 10px, #ffe066 20px)"
                />
            </P.Node>
        </P.Schematic>
    </P.Page>;
}
